/**
 * Centralized constants — collection paths, field names, magic numbers.
 *
 * Anything that's referenced from both /web and /functions belongs here.
 * Anything referenced only by one side stays in that side.
 */
// ---------------------------------------------------------------------------
// Firestore paths
// ---------------------------------------------------------------------------
export const COLLECTIONS = {
    USERS: 'users',
    USER_PUBLIC: 'userPublic',
    STATEMENTS: 'statements',
    COUNTRIES: 'countries',
    FX_RATES: 'fxRates',
    AUDIT_LOG: 'auditLog',
    /** One doc per day (YYYY-MM-DD): who the birthday Slack post already went out for. Server-only. */
    BIRTHDAY_SLACK_POSTS: 'birthdaySlackPosts',
    COMMENTS: 'comments',
    CONFIG: 'config',
    MAIL_DEAD_LETTER: 'mailDeadLetter',
    RESOURCES: 'resources',
    CREDIT_CARD_STATEMENTS: 'creditCardStatements',
    CREDIT_CARD_RECEIPT_LIBRARY: 'creditCardReceiptLibrary',
    /** Per-owner learned categorization (merchant → type/subtype/purpose/group). */
    CREDIT_CARD_MEMORY: 'creditCardMemory',
    /** Receipt requests: owner asks another user to upload a line-item receipt. */
    CREDIT_CARD_RECEIPT_REQUESTS: 'creditCardReceiptRequests',
    ABSENCE_REQUESTS: 'absenceRequests',
    /** In-app feedback (bug / improvement) with optional screenshot. */
    FEEDBACK: 'feedback',
    /** Latest compiled "feed to Claude Code" prompt (doc id 'latest'). */
    FEEDBACK_SUMMARY: 'feedbackSummary',
    /** Per-employee document vault metadata (files live in Storage). */
    EMPLOYEE_DOCUMENTS: 'employeeDocuments',
    DOCUMENT_SUBMISSIONS: 'documentSubmissions',
    CONTENT_SOURCES: 'contentSources',
    PAYPAL_IPN: 'paypalIpn',
    PAYPAL_IPN_META: 'paypalIpnMeta',
    OUTREACH_TEMPLATES: 'outreachTemplates',
    OUTREACH_MESSAGES: 'outreachMessages',
    OUTREACH_EVENTS: 'outreachEvents',
    OUTREACH_META: 'outreachMeta',
    OUTREACH_CAMPAIGNS: 'outreachCampaigns',
    PUBLIC_CARDS: 'publicCards',
    HUB_USAGE_DAILY: 'hubUsageDaily',
    HUB_USAGE_MONTHLY: 'hubUsageMonthly',
    HUB_USAGE_META: 'hubUsageMeta',
    CARD_LEADS: 'cardLeads',
    COMPANY_DOCUMENTS: 'companyDocuments',
    /** Four-eyes change requests for guarded config fields (Drive folder IDs). */
    CONFIG_CHANGE_REQUESTS: 'configChangeRequests',
    /** Time-tracking day entries (one per user per date). */
    TIME_ENTRIES: 'timeEntries',
    /** Monthly time statements submitted for manager approval. */
    TIME_STATEMENTS: 'timeStatements',
    /** Onboarding (and later offboarding) checklists assigned to employees. */
    ONBOARDING_CHECKLISTS: 'onboardingChecklists',
    LATE_PAYMENTS: 'latePayments',
    LATE_PAYMENTS_META: 'latePaymentsMeta',
    HOMEPAGE_BANNERS: 'homepageBanners',
    SIGNATURE_GUEST_ACCESS: 'signatureGuestAccess',
    /** DAAily Drivers — individual peer-recognition nominations. */
    DRIVER_NOMINATIONS: 'driverNominations',
    /** DAAily Drivers — one monthly cycle doc per 'YYYY-MM' (winners + status). */
    DRIVER_CYCLES: 'driverCycles',
    /** Admin-authored Home announcements (Oct 2026). */
    ANNOUNCEMENTS: 'announcements',
    /** Remote.com mirror (Oct 2026): server-only, admin/HR read. */
    REMOTE_TIMEOFF: 'remoteTimeoff',
    REMOTE_SYNC: 'remoteSync',
    REMOTE_EVENTS: 'remoteEvents',
    REMOTE_DOCUMENTS: 'remoteDocuments',
    /** DAAily Drivers — one doc per clap on a released winner (home Moments). Server-only. */
    DRIVER_CLAPS: 'driverClaps',
    /** Monthly Mutations — admin-only HR monthly notes/checklist entries. */
    MUTATIONS: 'mutations',
    /** Monthly Mutations per-month meta: Drive archive status + email-sent guard. */
    MUTATION_MONTHS: 'mutationMonths',
    /** Commissions — one doc per quarter (uploaded source metadata + results). */
    COMMISSION_PERIODS: 'commissionPeriods',
    /** Commissions — one computed report doc per quarter (`${year}-Q${q}`). */
    COMMISSION_REPORTS: 'commissionReports',
    /** Commissions — commission-paid tracking, one doc per `${year}__${bdKey}`. */
    COMMISSION_PAID: 'commissionPaid',
    /** Commissions — sourced monthly-average FX per month (`${YYYY-MM}`). */
    COMMISSION_FX_MONTHS: 'commissionFxMonths',
    /** Commissions — per-BD statement release records (`${period}__${bdKey}`). */
    COMMISSION_RELEASES: 'commissionReleases',
    /** Commissions — per-fiscal-year accrual ledger (`${fiscalYear}`). */
    COMMISSION_ACCRUALS: 'commissionAccruals',
    /** Commissions — per-assessment Accrual & Payout send records (`${period}`). */
    COMMISSION_ACCRUAL_SENDS: 'commissionAccrualSends',
    /** Commissions (Agents) — per-month computed report (`${YYYY-MM}`). */
    COMMISSION_AGENT_REPORTS: 'commissionAgentReports',
    /** Commissions (Agents) — per-month source metadata (`${YYYY-MM}`). */
    COMMISSION_AGENT_PERIODS: 'commissionAgentPeriods',
    /** Commissions (Agents) — per-month manual adjustments (`${YYYY-MM}`, map by agentKey). */
    COMMISSION_AGENT_ADJUSTMENTS: 'commissionAgentAdjustments',
    /** Commissions (Agents) — per-year paid tracking (`${year}__${agentKey}`). */
    COMMISSION_AGENT_PAID: 'commissionAgentPaid',
    /** Commissions (Agents) — released statements (`${YYYY-MM}__${agentKey}`). */
    COMMISSION_AGENT_RELEASES: 'commissionAgentReleases',
    /** Commissions (Agents) — Kirk-style Delta add-on ledger (`${agentKey}`). */
    COMMISSION_AGENT_DELTA: 'commissionAgentDelta',
    /** Commissions — OTHER layer (person-specific bonus plans, quarterly). */
    COMMISSION_OTHER_REPORTS: 'commissionOtherReports',
    /** FTE: Kostenstelle of every person at the end of a month (history for the FTE report). */
    FTE_KOSTENSTELLE_SNAPSHOTS: 'fteKostenstelleSnapshots',
    COMMISSION_OTHER_ADJUSTMENTS: 'commissionOtherAdjustments',
    COMMISSION_OTHER_RELEASES: 'commissionOtherReleases',
    /** USL — computed billed-revenue-vs-target dataset per year (`${year}`). */
    USL_REPORTS: 'uslReports',
    /** USL — raw source pull metadata per year (`${year}`). */
    USL_PERIODS: 'uslPeriods',
    /** USL — manual/external monthly streams (subscription/programmatic) per year (`${year}`). */
    USL_MANUAL: 'uslManual',
    /** USL — per-payment line detail for the segment drill-down (`${year}`). */
    USL_LINES: 'uslLines',
    USL_CLIENT_MIX: 'uslClientMix',
    /** USL — per-rep monthly BD forecasts (basic/stretched) per year (`${year}`). */
    USL_FORECASTS: 'uslForecasts',
    /** USL — open-payments snapshot (doc id 'current'), refreshed with every USL pull. */
    USL_OPEN_PAYMENTS: 'uslOpenPayments',
    /** Subscriptions — computed daily+monthly dataset per year (`${year}`). */
    SUBSCRIPTION_REPORTS: 'subscriptionReports',
    /** Subscriptions — raw Stripe-export upload/pull metadata per year (`${year}`). */
    SUBSCRIPTION_PERIODS: 'subscriptionPeriods',
    /** Subscriptions — in-flight/last Stripe revenue refresh per year (`${year}`), server-only writes.
     *  A slow Stripe report run is persisted here and completed by subscriptionRefreshFinisher. */
    SUBSCRIPTION_REFRESH_JOBS: 'subscriptionRefreshJobs',
    SUBSCRIPTION_REFRESH_ALL: 'subscriptionRefreshAll',
    /** Subscriptions — health snapshot from the Stripe core API (single doc `latest`). */
    SUBSCRIPTION_HEALTH: 'subscriptionHealth',
    /** Subscriptions — traffic/paywall/funnel stats from BigQuery (single doc `latest`). */
    SUBSCRIPTION_STATS: 'subscriptionStats',
    /** Promo-code usage snapshot from Piano (Oct 2026). */
    SUBSCRIPTION_PROMOTIONS: 'subscriptionPromotions',
    // SUBSCRIPTION_CONVERSION removed Sep 2026 — Conversion moved to
    // analytics-service, computed live per request, no Firestore cache.
    /** Signatures tool — e-signature requests (parallel signers, server-only writes). */
    SIGNATURE_REQUESTS: 'signatureRequests',
    /** Signatures tool — reusable document templates (placeholders + role-fields). */
    SIGNATURE_TEMPLATES: 'signatureTemplates',
    /** Signatures tool — company stamps managed by signature admins, each with the people allowed to use it (Oct 2026). */
    SIGNATURE_STAMPS: 'signatureStamps',
    /** Server-only settings of outbound integrations (Oct 2026: `commandCenterFeed`). */
    INTEGRATIONS: 'integrations',
    /** BQ Status — new-table alerts (email + bell to analytics admins). */
    BQ_STATUS_ALERTS: 'bqStatusAlerts',
    /** ClickUp PSM dashboard snapshot (single doc 'latest'). */
    CLICKUP_PSM: 'clickupPsmDashboard',
    /** BQ Status — scan watermark (seen table keys) + meta, doc id 'state'. */
    BQ_STATUS_META: 'bqStatusMeta',
    /** Product Projects — one doc per project initiative. */
    PRODUCT_PROJECTS: 'productProjects',
    /** Newsletter tool config docs (issues live in a subcollection). */
    NEWSLETTERS: 'newsletters',
    DIRECTOR_TOPICS: 'directorTopics',
    DIRECTOR_ACTIONS: 'directorActions',
    DIRECTOR_MEETINGS: 'directorMeetings',
    /** Directors' Room company team meeting sessions (doc id = YYYY-MM-DD). */
    DIRECTOR_COMPANY_MEETINGS: 'directorCompanyMeetings',
    /** Directors' Room company team meeting agenda rows. */
    DIRECTOR_COMPANY_ITEMS: 'directorCompanyItems',
    /** Directors' Room recording / Gemini notes / transcript summary per meeting (doc id = YYYY-MM-DD). */
    DIRECTOR_MEETING_MEDIA: 'directorMeetingMedia',
    /** Project Action Items — cross-project tasks, top-level for assignee/overdue queries. */
    PROJECT_ACTION_ITEMS: 'projectActionItems',
};
export const SUBCOLLECTIONS = {
    LINE_ITEMS: 'lineItems',
    /** Tasks under an onboarding checklist. */
    ONBOARDING_TASKS: 'tasks',
    /** Per-field change log under a project doc. */
    PROJECT_CHANGE_HISTORY: 'changeHistory',
};
/** Document ID for the singleton config document. */
export const CONFIG_DOC_ID = 'app';
/**
 * Compensation figures live in /config/restricted, not /config/app (security audit
 * Oct 2026): /config/app is readable by every signed-in user, collaborators too, and
 * held each BD's targets, agent rates and add-ons, and USL rep targets. The server
 * (getAppConfig) and the web config readers merge this doc over /config/app, so code
 * that reads `config.commissionYears` etc. is unchanged. Only commission/USL managers
 * and admins can read it (firestore.rules).
 */
export const RESTRICTED_CONFIG_DOC_ID = 'restricted';
export const RESTRICTED_CONFIG_FIELDS = [
    'commissionYears', 'commissionTargets', 'commissionAgents', 'commissionAgentRates', 'commissionOtherPlans', 'uslYears', 'uslTargets',
];
// ---------------------------------------------------------------------------
// Numeric precision
// ---------------------------------------------------------------------------
export const FX_RATE_DECIMALS = 6;
export const CURRENCY_AMOUNT_DECIMALS = 2;
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/** Max submissions per user per hour — prevents abuse. */
export const SUBMISSION_RATE_LIMIT_PER_HOUR = 10;
/** Receipt upload size cap before client-side compression (bytes). */
export const MAX_RECEIPT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB pre-compression
/** Target receipt size after client-side compression (bytes). */
export const TARGET_COMPRESSED_RECEIPT_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB
// ---------------------------------------------------------------------------
// Allowed receipt MIME types
// ---------------------------------------------------------------------------
export const ALLOWED_RECEIPT_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
    'application/pdf',
];
// ---------------------------------------------------------------------------
// Timezone for scheduled jobs
// ---------------------------------------------------------------------------
export const SCHEDULER_TZ = 'Europe/Zurich';
//# sourceMappingURL=constants.js.map